/**
 * Application Service
 * 
 * Handles all application-related API operations
 */

import apiClient from '../api/apiClient';
import config from '../config/appConfig';
import i18n from '../config/i18nConfig';
import { ApplicationContract } from "../contracts/application";
import { ApplicationListContract } from "../contracts/applicationList";
import { ScopeContract } from "../contracts/scope";
import { ScopeListContract } from "../contracts/scopeList";
import { SecretContract } from "../contracts/secret";

/**
 * Empty UID placeholder - used in API paths instead of actual user ID.
 * The backend does not require the real user ID; it uses the auth token.
 */
const EMPTY_UID = "00000000-0000-0000-0000-000000000000";

/**
 * Application Service Class
 * Provides both instance and static methods
 */
export class ApplicationService {
    constructor() { }

    // ========================================================================
    // Instance Methods
    // ========================================================================

    public async getApplications(userId: string, environment: string): Promise<ApplicationContract[]> {
        try {
            const query = `/appmgr/users/${EMPTY_UID}/applications?environment-name=${environment}`;
            const result = await apiClient.get<ApplicationListContract>(query);
            return result.applications || [];
        } catch (error: any) {
            // Handle 404 as an empty array (user has no applications)
            if (error?.code === "ResourceNotFound" || error?.status === 404) {
                return [];
            }
            throw new Error(i18n.t('errors.loadApplications'));
        }
    }

    public async getScopes(): Promise<ScopeContract[]> {
        try {
            const query = `/appmgr/scopes`;
            const result = await apiClient.get<ScopeListContract>(query);
            return result.scopes || [];
        } catch (error: any) {
            // Handle 404 as an empty array
            if (error?.code === "ResourceNotFound" || error?.status === 404) {
                return [];
            }
            throw new Error(i18n.t('errors.loadScopes'));
        }
    }

    public async createApplication(userId: string, request: ApplicationContract): Promise<ApplicationContract> {
        try {
            const query = `/appmgr/users/${EMPTY_UID}/applications`;
            const result = await apiClient.post<ApplicationContract>(query, request);
            return result;
        } catch (error: any) {
            throw new Error(i18n.t('errors.createApplication'));
        }
    }

    public async updateApplication(userId: string, request: ApplicationContract): Promise<ApplicationContract> {
        try {
            const query = `/appmgr/users/${EMPTY_UID}/applications/${request.id}`;
            const result = await apiClient.put<ApplicationContract>(query, request);
            return result;
        } catch (error: any) {
            throw new Error(i18n.t('errors.updateApplication'));
        }
    }

    public async deleteApplication(userId: string, applicationId: string): Promise<boolean> {
        try {
            const query = `/appmgr/users/${EMPTY_UID}/applications/${applicationId}`;
            await apiClient.delete(query);
            return true;
        } catch (error: any) {
            throw new Error(i18n.t('errors.deleteApplication'));
        }
    }

    public async createSecret(userId: string, applicationId: string, request: SecretContract): Promise<SecretContract> {
        try {
            const query = `/appmgr/users/${EMPTY_UID}/applications/${applicationId}/secrets`;
            const result = await apiClient.post<SecretContract>(query, request);
            return result;
        } catch (error: any) {
            throw new Error(i18n.t('errors.createSecret'));
        }
    }

    public async deleteSecret(userId: string, applicationId: string, secretId: string): Promise<boolean> {
        try {
            const query = `/appmgr/users/${EMPTY_UID}/applications/${applicationId}/secrets/${secretId}`;
            await apiClient.delete(query);
            return true;
        } catch (error: any) {
            throw new Error(i18n.t('errors.deleteSecret'));
        }
    }

    public async createScope(userId: string, applicationId: string, request: ScopeContract): Promise<ScopeContract> {
        try {
            const query = `/appmgr/users/${EMPTY_UID}/applications/${applicationId}/scopes`;
            const result = await apiClient.post<ScopeContract>(query, request);
            return result;
        } catch (error: any) {
            throw new Error(i18n.t('errors.addScope'));
        }
    }

    public async deleteScope(userId: string, applicationId: string, scopeId: string): Promise<boolean> {
        try {
            const query = `/appmgr/users/${EMPTY_UID}/applications/${applicationId}/scopes/${scopeId}`;
            await apiClient.delete(query);
            return true;
        } catch (error: any) {
            throw new Error(i18n.t('errors.removeScope'));
        }
    }

    public async addScope(userId: string, applicationId: string, scopeId: string): Promise<ScopeContract> {
        const request: ScopeContract = { 
            id: scopeId,
            code: scopeId,
            name: scopeId,
            description: '',
            isChecked: true
        };
        return this.createScope(userId, applicationId, request);
    }

    public async removeScope(userId: string, applicationId: string, scopeId: string): Promise<boolean> {
        return this.deleteScope(userId, applicationId, scopeId);
    }

    // ========================================================================
    // Static Methods (for backward compatibility)
    // ========================================================================

    public static async getApplications(userId: string, environment: string): Promise<ApplicationContract[]> {
        return applicationService.getApplications(userId, environment);
    }

    public static async getScopes(): Promise<ScopeContract[]> {
        return applicationService.getScopes();
    }

    public static async createApplication(userId: string, request: ApplicationContract): Promise<ApplicationContract> {
        return applicationService.createApplication(userId, request);
    }

    public static async updateApplication(userId: string, applicationId: string, updates: Partial<ApplicationContract>): Promise<ApplicationContract> {
        const fullRequest = { ...updates, id: applicationId } as ApplicationContract;
        return applicationService.updateApplication(userId, fullRequest);
    }

    public static async deleteApplication(userId: string, applicationId: string): Promise<boolean> {
        return applicationService.deleteApplication(userId, applicationId);
    }

    public static async createSecret(userId: string, applicationId: string, request: SecretContract): Promise<SecretContract> {
        return applicationService.createSecret(userId, applicationId, request);
    }

    public static async deleteSecret(userId: string, applicationId: string, secretId: string): Promise<boolean> {
        return applicationService.deleteSecret(userId, applicationId, secretId);
    }

    public static async createScope(userId: string, applicationId: string, request: ScopeContract): Promise<ScopeContract> {
        return applicationService.createScope(userId, applicationId, request);
    }

    public static async deleteScope(userId: string, applicationId: string, scopeId: string): Promise<boolean> {
        return applicationService.deleteScope(userId, applicationId, scopeId);
    }

    public static async addScope(userId: string, applicationId: string, scopeId: string): Promise<ScopeContract> {
        return applicationService.addScope(userId, applicationId, scopeId);
    }

    public static async removeScope(userId: string, applicationId: string, scopeId: string): Promise<boolean> {
        return applicationService.removeScope(userId, applicationId, scopeId);
    }
}

export const applicationService = new ApplicationService();
export default applicationService;
